/**
 * PreferenciasTabelasProvider (Bloco TBL.2).
 *
 * Provider singleton que gerencia preferencias de tabela cross-device:
 *   - larguras de colunas por tabela (substitui localStorage por-device)
 *   - estado UI compartilhado (ex.: precos_solicitados — TBL.6)
 *
 * Fluxo:
 *   - Mount: 1 GET pro backend para carregar cache em memoria
 *   - Operacoes (get/set) consultam/atualizam cache imediatamente
 *   - Save diferido: dispara 1 PUT no cleanup do provider (navegacao
 *     SPA / unmount) se houve diff vs ultimo snapshot persistido
 *   - Fallback localStorage silencioso: se backend nao responde, le/grava
 *     no `pharuerp:col-width:v1:{tableId}` legado (sem perder UX)
 *
 * NAO usa navigator.sendBeacon (TBL-D1): fechar aba sem navegar perde
 * mudancas daquela sessao. Aceitavel; documentado no plano.
 *
 * API:
 *   const ctx = usePreferenciasTabelas();
 *   ctx.getTabelaWidths("ppcp")           // {cliente: 240, ...} ou {}
 *   ctx.setTabelaWidths("ppcp", {cliente: 250})
 *   ctx.getUI()                            // {precos_solicitados: true, ...}
 *   ctx.setUIPartial({precos_solicitados: false})
 *   ctx.flush()                            // forca PUT agora (raro)
 *   ctx.flush({propagarErro: true})        // idem, mas REJEITA se o PUT falhar
 *                                          // (use em botao explicito de salvar,
 *                                          //  p/ nao mostrar "salvo" falso)
 *   ctx.estado                             // 'loading'|'pronto'|'erro_inicial'
 *
 * Nota: o flush so envia com `estado === 'pronto'`. Em 'erro_inicial' o GET
 * falhou e o cache esta vazio — salvar sobrescreveria (apagando) todas as
 * preferencias do usuario no servidor.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  obterPreferenciasTabelas,
  salvarPreferenciasTabelas,
} from "../services/preferenciasTabelas";

const PreferenciasTabelasContext = createContext(null);

// localStorage legado (compatibilidade enquanto preferencias migram).
const LEGADO_PREFIX = "pharuerp:col-width:v1:";

function lerWidthsLegadoLocalStorage(tabelaId) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LEGADO_PREFIX + tabelaId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "number" && Number.isFinite(v) && v >= 40) {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function salvarWidthsLegadoLocalStorage(tabelaId, widths) {
  if (typeof window === "undefined") return;
  try {
    const payload = {};
    for (const [k, v] of Object.entries(widths)) {
      if (typeof v === "number") payload[k] = Math.round(v);
    }
    if (Object.keys(payload).length === 0) {
      window.localStorage.removeItem(LEGADO_PREFIX + tabelaId);
    } else {
      window.localStorage.setItem(
        LEGADO_PREFIX + tabelaId,
        JSON.stringify(payload)
      );
    }
  } catch {
    /* quota cheia / indisponivel — degrada */
  }
}

/**
 * Normaliza chaves recursivamente — necessario porque JSON.stringify
 * nao tem ordem garantida e diff por string falha se chaves vierem
 * em ordens distintas entre dois snapshots.
 */
function normalizarParaDiff(obj) {
  if (Array.isArray(obj)) return obj.map(normalizarParaDiff);
  if (obj && typeof obj === "object") {
    const out = {};
    for (const k of Object.keys(obj).sort()) {
      out[k] = normalizarParaDiff(obj[k]);
    }
    return out;
  }
  return obj;
}

function ehDiferente(a, b) {
  return JSON.stringify(normalizarParaDiff(a)) !== JSON.stringify(normalizarParaDiff(b));
}

export function PreferenciasTabelasProvider({ children }) {
  const { request, authenticated } = useAuth();

  // Estado consolidado em memoria (cache). Ref evita re-renders desnecessarios
  // em consumers que so leem (set dispara update via setVersao).
  const cacheRef = useRef({ tabelas: {}, ui: {} });
  // Snapshot do que ja esta salvo no backend (pra calcular diff no flush).
  const ultimoSalvoRef = useRef({ tabelas: {}, ui: {} });
  // BUG-TBL.flush (2026-05-25): debounce 2s pos-mutacao. Sem isso, o flush
  // so disparava no unmount do provider (logout/fechar aba) — como
  // PreferenciasTabelasProvider envolve AuthenticatedShell, navegacao SPA
  // NUNCA desmontava, e a usuaria perdia preferencias ao recarregar pagina.
  // Debounce: cada setTabelaWidths/setUIPartial agenda flush em 2s; nova
  // mutacao no intervalo cancela e re-agenda. Garante save mesmo sem
  // navegacao. Aceita perder mudancas se reload <2s pos-ultima mudanca
  // (TBL-D1: aceitavel).
  const flushTimeoutRef = useRef(null);
  // Versao incrementada a cada mutacao — usado por consumers que precisam reagir
  // (ex.: PrecosContext lendo precos_solicitados).
  const [versao, setVersao] = useState(0);
  const [estado, setEstado] = useState("loading"); // loading | pronto | erro_inicial

  // Carrega backend 1x na primeira montagem com auth pronta.
  useEffect(() => {
    if (!authenticated) {
      setEstado("loading");
      return;
    }
    let cancelado = false;
    obterPreferenciasTabelas(request)
      .then((payload) => {
        if (cancelado) return;
        const prefs = payload?.preferencias_tabelas || {};
        cacheRef.current = {
          tabelas: prefs.tabelas || {},
          ui: prefs.ui || {},
          ...prefs, // preserva chaves desconhecidas para compat futuro
        };
        ultimoSalvoRef.current = normalizarParaDiff(cacheRef.current);
        setEstado("pronto");
        setVersao((v) => v + 1);
      })
      .catch(() => {
        if (cancelado) return;
        // Backend indisponivel: cache fica vazio (legado lido sob demanda
        // por getTabelaWidths). UX nao quebra.
        setEstado("erro_inicial");
      });
    return () => {
      cancelado = true;
    };
  }, [authenticated, request]);

  const getTabelaWidths = useCallback((tabelaId) => {
    if (!tabelaId) return {};
    const fromBackend = cacheRef.current?.tabelas?.[tabelaId]?.widths;
    if (fromBackend && Object.keys(fromBackend).length > 0) return { ...fromBackend };
    // Fallback localStorage legado: usuario que nunca salvou no backend
    // mas tem larguras antigas em localStorage continua vendo elas.
    return lerWidthsLegadoLocalStorage(tabelaId);
  }, []);

  const setTabelaWidths = useCallback((tabelaId, widthsDict) => {
    if (!tabelaId) return;
    const widthsLimpos = {};
    for (const [k, v] of Object.entries(widthsDict || {})) {
      if (typeof v === "number" && Number.isFinite(v) && v >= 40) {
        widthsLimpos[k] = Math.round(v);
      }
    }
    if (!cacheRef.current.tabelas) cacheRef.current.tabelas = {};
    cacheRef.current.tabelas[tabelaId] = { widths: widthsLimpos };
    // Espelha no localStorage legado (cache offline + fallback se backend cair)
    salvarWidthsLegadoLocalStorage(tabelaId, widthsLimpos);
    setVersao((v) => v + 1);
    agendarFlushDebouncedRef.current?.();
  }, []);

  const getUI = useCallback(() => {
    return { ...(cacheRef.current?.ui || {}) };
  }, []);

  const setUIPartial = useCallback((parcial) => {
    if (!parcial || typeof parcial !== "object") return;
    cacheRef.current.ui = { ...(cacheRef.current.ui || {}), ...parcial };
    setVersao((v) => v + 1);
    agendarFlushDebouncedRef.current?.();
  }, []);

  // Flush: envia PUT se houve mudanca vs ultimo salvo. Idempotente —
  // chamado no cleanup do provider (unmount) e tambem explicitamente
  // por consumers em momentos criticos (ex.: navegacao manual).
  const flush = useCallback(async (opcoes = {}) => {
    // SÓ salva com o cache hidratado do backend. Em 'erro_inicial' o GET
    // falhou e o cache é o literal vazio {tabelas:{},ui:{}} — um PUT aqui
    // SOBRESCREVERIA o campo inteiro no servidor, apagando as larguras de
    // coluna de TODAS as tabelas e as flags de UI do usuário. Degradar
    // (não persistir nesta sessão) é o comportamento correto.
    if (estado !== "pronto") return;
    if (!authenticated) return;
    const atual = cacheRef.current;
    if (!ehDiferente(atual, ultimoSalvoRef.current)) return;
    try {
      await salvarPreferenciasTabelas(request, atual);
      ultimoSalvoRef.current = normalizarParaDiff(atual);
    } catch (e) {
      // Caminhos automáticos (debounce/unmount) aceitam perda silenciosa —
      // o cache legado em localStorage cobre as larguras. Já quem salva por
      // um BOTÃO explícito passa `propagarErro` para poder avisar o usuário
      // em vez de mostrar um "salvo" falso.
      if (opcoes.propagarErro) throw e;
    }
  }, [authenticated, estado, request]);

  // BUG-TBL.flush (2026-05-25): agenda flush debounced 2s pos-mutacao.
  // Mantemos como ref para que setTabelaWidths/setUIPartial (definidos
  // antes do `flush`) consigam chamar sem dependencia circular.
  const agendarFlushDebouncedRef = useRef(null);
  useEffect(() => {
    agendarFlushDebouncedRef.current = () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = setTimeout(() => {
        flushTimeoutRef.current = null;
        flush();
      }, 2000);
    };
  }, [flush]);

  // Salva ao desmontar o provider (logout, navegacao raiz). Cleanup
  // tambem cancela qualquer timeout debounced pendente.
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
      flush();
    };
  }, [flush]);

  // Salva tambem em eventos de "navegacao" — opcional, defensivo.
  // O cleanup acima cobre o caso comum.

  const value = useMemo(
    () => ({
      getTabelaWidths,
      setTabelaWidths,
      getUI,
      setUIPartial,
      flush,
      estado,
      versao, // consumers que reagem a mudancas dependem disso no useMemo
    }),
    [getTabelaWidths, setTabelaWidths, getUI, setUIPartial, flush, estado, versao]
  );

  return (
    <PreferenciasTabelasContext.Provider value={value}>
      {children}
    </PreferenciasTabelasContext.Provider>
  );
}

export function usePreferenciasTabelas() {
  const ctx = useContext(PreferenciasTabelasContext);
  if (!ctx) {
    // Defesa: se algum componente fora do provider tentar consumir,
    // retorna API no-op (UX nao quebra).
    return {
      getTabelaWidths: () => ({}),
      setTabelaWidths: () => {},
      getUI: () => ({}),
      setUIPartial: () => {},
      flush: async () => {},
      estado: "loading",
      versao: 0,
    };
  }
  return ctx;
}
